/**
 * BulletStyleSections - Collapsible style sections for bullet configuration
 *
 * Renders multiple collapsible accordions for:
 * - Item state (background, border, radius)
 * - Item padding
 * - List padding
 * - Title font styles
 * - Subtitle font styles
 * - Image styles
 *
 * Note: Uses MUI Accordion directly (not SAccordion) to allow nesting
 * inside parent SAccordion. SAccordion prevents nesting by design.
 *
 * Key feature: Style changes to items.0.* are propagated to ALL items
 * for consistency (same pattern as ListStyleSections).
 *
 * Sprint: screen-editor-paywall
 * Task: Fix Bullets Section
 */

import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PaddingElement, PaddingValues } from '../../../../factories/elements';
import { StyleFieldRow } from '../../../shared/styleFields/StyleFieldRow';
import { BulletStyleSectionConfig } from '../../../../config/v2PaywallBodyConfig';
import { FIELD_STYLE_GRID_GAP } from '../../../../config/ui';
import { EXPANDED_SECTION_BGCOLOR } from '../../../../config/editorFieldStyles';
import { getStyleCheckboxTestId, getStyleInputTestId } from '../../../../utils/testIdUtils';

/**
 * Props for BulletStyleSections
 */
export interface BulletStyleSectionsProps {
  /** Style section configurations */
  sections: BulletStyleSectionConfig[];

  /** Full struct object for reading values */
  struct: Record<string, unknown>;

  /** Callback when a value changes */
  onChange: (path: string, value: unknown) => void;

  /** Whether editing is disabled */
  disabled?: boolean;

  /** Path to items array for style propagation (default: 'list.items') */
  itemsPath?: string;

  /** When true, padding reads/writes use V2 EdgeInsets (top/bottom/start/end) instead of V1 (paddingTop/Right/Bottom/Left) */
  useV2Insets?: boolean;
}

/**
 * Get value at a dot-notation path from an object
 */
function getValueAtPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Extract padding values from struct at given base path.
 * When useV2Insets=true, reads EdgeInsets fields (top/bottom/start/end).
 */
function getPaddingValues(struct: Record<string, unknown>, basePath: string, useV2Insets = false): PaddingValues {
  const styles = getValueAtPath(struct, basePath) as Record<string, unknown> | undefined;

  if (useV2Insets) {
    return {
      paddingTop: typeof styles?.top === 'number' ? styles.top : 0,
      paddingRight: typeof styles?.end === 'number' ? styles.end : 0,
      paddingBottom: typeof styles?.bottom === 'number' ? styles.bottom : 0,
      paddingLeft: typeof styles?.start === 'number' ? styles.start : 0,
    };
  }

  return {
    paddingTop: typeof styles?.paddingTop === 'number' ? styles.paddingTop : 0,
    paddingRight: typeof styles?.paddingRight === 'number' ? styles.paddingRight : 0,
    paddingBottom: typeof styles?.paddingBottom === 'number' ? styles.paddingBottom : 0,
    paddingLeft: typeof styles?.paddingLeft === 'number' ? styles.paddingLeft : 0,
  };
}

/**
 * Translate V1 padding field names to V2 EdgeInsets field names in path.
 */
function translatePaddingPath(path: string): string {
  return path
    .replace('.paddingTop', '.top')
    .replace('.paddingRight', '.end')
    .replace('.paddingBottom', '.bottom')
    .replace('.paddingLeft', '.start');
}

/**
 * BulletStyleSections - Renders collapsible style accordions
 *
 * Uses MUI Accordion directly to avoid SAccordion nesting restriction.
 */
export const BulletStyleSections: React.FC<BulletStyleSectionsProps> = ({
  sections,
  struct,
  onChange,
  disabled = false,
  itemsPath = 'list.items',
  useV2Insets = false,
}) => {
  const theme = useTheme();

  /**
   * When updating item-specific styles (title, subtitle, image),
   * propagate the change to ALL items in the list, not just item 0.
   *
   * Detects paths like 'list.items.0.title.styles.fontSize' and applies
   * to all items: items.0, items.1, items.2, etc.
   */
  const handleItemStyleChange = useCallback(
    (path: string, value: unknown) => {
      // Check if this is an item-specific style path (contains .items.0.)
      if (path.includes('.items.0.')) {
        // Get the items array to know how many items exist
        const items = getValueAtPath(struct, itemsPath) as unknown[] | undefined;
        if (items && items.length > 0) {
          // Apply to all items by calling onChange for each
          items.forEach((_, index) => {
            const itemPath = path.replace('.items.0.', `.items.${index}.`);
            onChange(itemPath, value);
          });
          return;
        }
      }
      // Not an item path, update normally
      onChange(path, value);
    },
    [struct, onChange, itemsPath]
  );

  // Handle padding change - uses handleItemStyleChange to propagate to all items
  // When useV2Insets, translates paddingTop→top, paddingLeft→start, paddingRight→end, paddingBottom→bottom
  const handlePaddingChange = useCallback(
    (path: string, value: number) => {
      const translatedPath = useV2Insets ? translatePaddingPath(path) : path;
      handleItemStyleChange(translatedPath, value);
    },
    [handleItemStyleChange, useV2Insets]
  );

  // Memoize visible sections
  const visibleSections = useMemo(() => sections, [sections]);

  if (visibleSections.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
      data-testid="bullet-style-sections"
    >
      {visibleSections.map((section) => (
        <Accordion
          key={section.id}
          defaultExpanded={section.defaultExpanded}
          disableGutters
          elevation={0}
          data-testid={`bullet-style-section-${section.id}`}
          sx={{
            backgroundColor: 'transparent',
            '&:before': { display: 'none' },
            '& .MuiAccordionSummary-root': {
              minHeight: 32,
              px: 1,
              py: 0,
              backgroundColor: theme.palette.action.hover,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: theme.palette.action.selected,
              },
            },
            '& .MuiAccordionSummary-content': {
              margin: '4px 0',
            },
            '& .MuiAccordionDetails-root': {
              px: 2,
              pt: 1,
              pb: 1,
              bgcolor: EXPANDED_SECTION_BGCOLOR,
              borderRadius: 1,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            aria-controls={`${section.id}-content`}
            id={`${section.id}-header`}
          >
            <Typography variant="caption" fontWeight={500}>
              {section.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Render fields in 2-column grid if they exist */}
            {section.fields && section.fields.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: FIELD_STYLE_GRID_GAP,
                  mb: section.paddingConfig ? 1.5 : 0,
                }}
                data-testid={`bullet-style-section-${section.id}-fields`}
              >
                {section.fields.map((fieldConfig) => (
                  <StyleFieldRow
                    key={fieldConfig.path}
                    config={fieldConfig}
                    value={getValueAtPath(struct, fieldConfig.path)}
                    onChange={(value) => handleItemStyleChange(fieldConfig.path, value)}
                    disabled={disabled}
                    inputTestId={getStyleInputTestId(section.id, fieldConfig.label)}
                    checkboxTestId={fieldConfig.isNullable ? getStyleCheckboxTestId(section.id, fieldConfig.label) : undefined}
                  />
                ))}
              </Box>
            )}

            {/* Render padding element if configured (after fields) */}
            {section.paddingConfig && (
              <PaddingElement
                basePath={section.paddingConfig.basePath}
                values={getPaddingValues(struct, section.paddingConfig.basePath, useV2Insets)}
                onChange={handlePaddingChange}
                label={section.paddingConfig.label}
                disabled={disabled}
                defaultExpanded={true}
              />
            )}

            {/* Placeholder for empty sections (no fields and no padding) */}
            {(!section.fields || section.fields.length === 0) && !section.paddingConfig && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontStyle: 'italic' }}
                data-testid={`bullet-style-section-${section.id}-placeholder`}
              >
                Style fields coming soon
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default BulletStyleSections;
